import { getStoredUnits } from '@/lib/units';

export interface WatchLiveMetrics {
  avgHeartRateBpm: number;
  currentHeartRate: number;
  zoneSeconds: { z1: number; z2: number; z3: number; z4: number; z5: number };
  hrSamples: { timestamp: number; bpm: number }[];
  elapsedSeconds: number;
  distanceMeters?: number;
  /** Watch AI Guide "CURRENT PACE" (distance-window). */
  livePaceSecondsPerKm?: number;
  /** Watch main "ROLLING KM" pace. */
  currentPaceSecondsPerKm?: number;
  splits?: { km: number; elapsedSeconds: number; paceSecondsPerKm: number }[];
  segments: { index: number; durationSeconds: number; distanceMeters: number; avgPaceSecondsPerKm: number; avgHeartRateBpm?: number }[];
}

export interface WatchCompletedWorkout {
  id: string;
  kind: string;
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
  distanceMeters?: number;
  avgPaceSecondsPerKm?: number;
  avgHeartRateBpm?: number;
  maxHeartRateBpm?: number;
  splits: { km: number; elapsedSeconds: number; paceSecondsPerKm: number }[];
  segments: { index: number; durationSeconds: number; distanceMeters: number; avgPaceSecondsPerKm: number; avgHeartRateBpm?: number }[];
  hrSamples: { timestamp: number; bpm: number }[];
  zoneSeconds: { z1: number; z2: number; z3: number; z4: number; z5: number };
  hkWorkoutUUID?: string;
}

export interface StraivWatchPlugin {
  isWatchAppInstalled(): Promise<{ installed: boolean; reachable: boolean }>;
  syncProfileToWatch(options: {
    userId: string;
    ageYears?: number;
    maxHrMeasured?: number;
    units?: 'metric' | 'imperial';
  }): Promise<void>;
  syncAuthToWatch(options: {
    accessToken: string;
    refreshToken?: string;
    supabaseUrl: string;
    supabaseAnonKey: string;
  }): Promise<void>;
  syncGymPlanToWatch(options: { plan: { title: string; exercises: unknown[] } }): Promise<void>;
  startPhoneWorkoutSession(): Promise<void>;
  startPhoneGymSession(): Promise<void>;
  endPhoneWorkoutSession(): Promise<void>;
  signalWatchRunEnd(): Promise<void>;
  signalWatchGymEnd(): Promise<void>;
  markSegmentOnWatch(): Promise<void>;
  pauseWatchRun(): Promise<void>;
  resumeWatchRun(): Promise<void>;
  pushPhoneDistanceToWatch(options: { distanceMeters: number }): Promise<void>;
  pushCoachGuideToWatch(options: {
    active: boolean;
    headline?: string;
    isRest?: boolean;
    metricMode?: 'distance' | 'time';
    distanceMeters?: number;
    blockStartDistanceMeters?: number;
    distanceIntoBlockMeters?: number;
    remainingDistanceMeters?: number;
    remainingSeconds?: number;
    blockEndsAtRunElapsed?: number;
    isComplete?: boolean;
    completeSummary?: string;
  }): Promise<void>;
  getWatchLiveMetrics(): Promise<WatchLiveMetrics | Record<string, never>>;
  getLastCompletedWatchWorkout(): Promise<WatchCompletedWorkout | Record<string, never>>;
  clearLastCompletedWatchWorkout(): Promise<void>;
  addListener(
    eventName: 'watchLiveMetrics' | 'watchRunEnded' | 'watchRunPaused' | 'watchRunResumed',
    listenerFunc: (data: WatchLiveMetrics | Record<string, never>) => void,
  ): Promise<{ remove: () => void }>;
}

function getPlugin(): StraivWatchPlugin | undefined {
  if (typeof window === 'undefined') return undefined;
  const cap = (window as unknown as { Capacitor?: { Plugins?: { StraivWatch?: StraivWatchPlugin } } }).Capacitor;
  return cap?.Plugins?.StraivWatch;
}

export async function isWatchAppInstalled(): Promise<boolean> {
  const plugin = getPlugin();
  if (!plugin) return false;
  try {
    const r = await plugin.isWatchAppInstalled();
    return r.installed;
  } catch {
    return false;
  }
}

export async function syncWatchProfile(
  userId: string,
  ageYears?: number,
  maxHrMeasured?: number,
  units?: 'metric' | 'imperial',
): Promise<void> {
  const plugin = getPlugin();
  if (!plugin) return;
  const resolvedUnits =
    units === 'imperial' || units === 'metric' ? units : getStoredUnits();
  await plugin.syncProfileToWatch({
    userId,
    ageYears,
    maxHrMeasured,
    units: resolvedUnits,
  });
}

export async function syncWatchAuth(
  accessToken: string,
  supabaseUrl: string,
  supabaseAnonKey: string,
  refreshToken?: string,
): Promise<void> {
  const plugin = getPlugin();
  if (!plugin) return;
  await plugin.syncAuthToWatch({ accessToken, refreshToken, supabaseUrl, supabaseAnonKey });
}

export async function syncGymPlanToWatch(plan: { title: string; exercises: unknown[] }): Promise<void> {
  const plugin = getPlugin();
  if (!plugin) return;
  await plugin.syncGymPlanToWatch({ plan });
}

export async function startPhoneWorkoutSession(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.startPhoneWorkoutSession) return;
  await plugin.startPhoneWorkoutSession();
}

export async function startPhoneGymSession(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.startPhoneGymSession) return;
  await plugin.startPhoneGymSession();
}

export async function endPhoneWorkoutSession(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.endPhoneWorkoutSession) return;
  await plugin.endPhoneWorkoutSession();
}

export async function signalWatchRunEnd(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.signalWatchRunEnd) return;
  await plugin.signalWatchRunEnd();
}

export async function signalWatchGymEnd(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.signalWatchGymEnd) return;
  await plugin.signalWatchGymEnd();
}

export async function markSegmentOnWatch(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.markSegmentOnWatch) return;
  await plugin.markSegmentOnWatch();
}

export async function pauseWatchRun(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.pauseWatchRun) return;
  try {
    await plugin.pauseWatchRun();
  } catch {
    /* ignore */
  }
}

export async function resumeWatchRun(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.resumeWatchRun) return;
  try {
    await plugin.resumeWatchRun();
  } catch {
    /* ignore */
  }
}

/** Relay iPhone GPS distance so the watch session stays aligned with the phone route. */
export async function pushPhoneDistanceToWatch(distanceMeters: number): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.pushPhoneDistanceToWatch) return;
  try {
    await plugin.pushPhoneDistanceToWatch({ distanceMeters });
  } catch {
    /* ignore */
  }
}

export async function pushCoachGuideToWatch(payload: {
  active: boolean;
  headline?: string;
  isRest?: boolean;
  metricMode?: 'distance' | 'time';
  distanceMeters?: number;
  blockStartDistanceMeters?: number;
  distanceIntoBlockMeters?: number;
  remainingDistanceMeters?: number;
  remainingSeconds?: number;
  blockEndsAtRunElapsed?: number;
  isComplete?: boolean;
  completeSummary?: string;
}): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.pushCoachGuideToWatch) return;
  try {
    await plugin.pushCoachGuideToWatch(payload);
  } catch {
    /* ignore */
  }
}

export async function getWatchLiveMetrics(): Promise<WatchLiveMetrics | null> {
  const plugin = getPlugin();
  if (!plugin?.getWatchLiveMetrics) return null;
  try {
    const data = await plugin.getWatchLiveMetrics();
    if (!data || typeof (data as WatchLiveMetrics).elapsedSeconds !== 'number') return null;
    return data as WatchLiveMetrics;
  } catch {
    return null;
  }
}

export async function getLastCompletedWatchWorkout(): Promise<WatchCompletedWorkout | null> {
  const plugin = getPlugin();
  if (!plugin?.getLastCompletedWatchWorkout) return null;
  try {
    const data = await plugin.getLastCompletedWatchWorkout();
    if (!data || typeof (data as WatchCompletedWorkout).durationSeconds !== 'number') return null;
    return data as WatchCompletedWorkout;
  } catch {
    return null;
  }
}

export async function clearLastCompletedWatchWorkout(): Promise<void> {
  const plugin = getPlugin();
  if (!plugin?.clearLastCompletedWatchWorkout) return;
  try {
    await plugin.clearLastCompletedWatchWorkout();
  } catch {
    /* ignore */
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Prevent Capacitor bridge calls from hanging forever (e.g. HealthKit during end-of-run). */
export function withPluginTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

/** Poll App Group for the watch-completed workout (handles race with HealthKit end detection). */
export async function fetchLastCompletedWatchWorkoutForRun(
  runStartMs: number,
  timeoutMs = 5000,
): Promise<WatchCompletedWorkout | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const workout = await withPluginTimeout(getLastCompletedWatchWorkout(), 3500, null);
    if (workout) {
      const workoutStart = new Date(workout.startedAt).getTime();
      if (runStartMs <= 0 || Number.isNaN(workoutStart) || Math.abs(workoutStart - runStartMs) <= 180_000) {
        return workout;
      }
    }
    await sleep(250);
  }
  return null;
}

export async function addWatchLiveMetricsListener(
  cb: (metrics: WatchLiveMetrics) => void,
): Promise<(() => void) | undefined> {
  const plugin = getPlugin();
  if (!plugin?.addListener) return undefined;
  try {
    const handle = await plugin.addListener('watchLiveMetrics', (data) => {
      if (data && typeof (data as WatchLiveMetrics).elapsedSeconds === 'number') {
        cb(data as WatchLiveMetrics);
      }
    });
    return () => void handle.remove();
  } catch {
    return undefined;
  }
}

export async function addWatchRunEndedListener(
  cb: (workout: WatchCompletedWorkout | null) => void,
): Promise<(() => void) | undefined> {
  const plugin = getPlugin();
  if (!plugin?.addListener) return undefined;
  try {
    const handle = await plugin.addListener('watchRunEnded', (data) => {
      if (data && typeof (data as unknown as WatchCompletedWorkout).durationSeconds === 'number') {
        cb(data as unknown as WatchCompletedWorkout);
      } else {
        cb(null);
      }
    });
    return () => void handle.remove();
  } catch {
    return undefined;
  }
}

/** Fires when the user pauses (true) or resumes (false) the run on the Apple Watch. */
export async function addWatchPauseListener(
  cb: (paused: boolean) => void,
): Promise<(() => void) | undefined> {
  const plugin = getPlugin();
  if (!plugin?.addListener) return undefined;
  try {
    const pausedHandle = await plugin.addListener('watchRunPaused', () => cb(true));
    const resumedHandle = await plugin.addListener('watchRunResumed', () => cb(false));
    return () => {
      void pausedHandle.remove();
      void resumedHandle.remove();
    };
  } catch {
    return undefined;
  }
}
